import React, { useEffect, useMemo, useRef, useState } from "react";
import MetaTags from "react-meta-tags";
import SweetAlert from "react-bootstrap-sweetalert";
import { Card, CardBody, Col, Container, Modal, ModalBody, ModalHeader, Row } from "reactstrap";
import ToolkitProvider from "react-bootstrap-table2-toolkit";
import cellEditFactory, { Type } from "react-bootstrap-table2-editor";
import { AnimatePresence, motion } from "framer-motion";
import toastr from "toastr";
import ExcelJS from "exceljs";

import {
  AlertTriangle,
  ScrollText,
  Package,
  CheckCircle,
  AlertCircle,
  Trash2,
  BadgePlus,
  Copy,
  BicepsFlexed,
  FileBox,
  NotepadTextDashed,
  Rows2,
  MessageCircleReply,
  AlarmClock,
  Boxes,
  PackageMinus,
  File,
  HandCoins,
  FileDown,
  Mail,
  MailOpen,
  ChevronDown,
} from "lucide-react";

import { priorityMoscowApi } from "../../services/PriorityMoscowService";
import Breadcrumbs from "../../components/Breadcrumb";
import TrackingColumnPrTest from "../../components/TrackingColumnPrTest";
import { fetchTrackingStatuses } from "../../api/trackingApi";
import PageLoader from "../../components/Loaders/pageLoader";
import { showToast, showToastFixed } from "../../components/Toast";
import { sendEmailWithExcel } from "../../api/sendEmailExcel";
import { MainContent } from "../../components/Table/MainContent";

export default function PriorityMoscow({ profileData, loading }) {
  const {
    data = {},
    isLoading,
    error,
    refetch,
  } = priorityMoscowApi.useFetchAllPriorityMoscowQuery(undefined, {
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  const [showLogModal, setShowLogModal] = useState(false);
  const [logModalOrderId, setLogModalOrderId] = useState(null);

  const {
    data: logs = [],
    isLoading: logsLoading,
    refetch: refetchLogs,
  } = priorityMoscowApi.useGetLogsByOrderIdQuery(logModalOrderId, {
    skip: !showLogModal || !logModalOrderId,
    refetchOnMountOrArgChange: true,
  });

  const [updatePriorityMoscow] = priorityMoscowApi.useUpdatePriorityMoscowMutation();

  const [deletePriorityMoscow] = priorityMoscowApi.useDeletePriorityMoscowMutation();

  const [tableData, setTableData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);

  const [filters, setNoDbFilters] = useState({});

  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);

  const [cardHeight, setCardHeight] = useState(0);
  const cardRef = useRef(null);

  useEffect(() => {
    if (data.priorityMoscow) {
      setTableData(JSON.parse(JSON.stringify(data.priorityMoscow)));
    }
  }, [data]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (showLogModal && logModalOrderId) {
      refetchLogs();
    }
  }, [showLogModal, logModalOrderId, refetchLogs]);

  useEffect(() => {
    if (!cardRef.current) {
      return undefined;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const newHeight = entry.contentRect.height;

        if (newHeight > 0) {
          setCardHeight(newHeight);
        }
      }
    });

    resizeObserver.observe(cardRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const filteredData = useMemo(() => {
    let result = [...tableData];

    if (filters?.status?.length > 0) {
      result = result.filter((row) => filters.status.includes(row.status));
    } else {
      result = result.filter((row) => row.status !== "sent");
    }

    return result;
  }, [filters, tableData]);

  const handleSave = async (oldValue, newValue, row, column) => {
    if (oldValue === newValue) {
      return;
    }

    const field = column.dataField;
    let valueForServer = newValue;

    if (field === "date" && typeof newValue === "string" && newValue.length === 10) {
      const now = new Date();
      const [year, month, day] = newValue.split("-");

      const updatedDate = new Date(Number(year), Number(month) - 1, Number(day), now.getHours(), now.getMinutes());

      valueForServer = updatedDate.toISOString();
    }

    const updatedRow = {
      _id: row._id,
      [field]: valueForServer,
      invoiceNo: row.orderNumber,
      clientName: row.clientName,
      managerID: profileData?._id,

      ...(row.managers?.bitrixID
        ? {
            bitrixID: row.managers.bitrixID,
          }
        : {}),
    };

    if (field === "trackingNo") {
      const trackingNumbers =
        typeof newValue === "string"
          ? newValue
              .split(/[, .\n]+/)
              .map((number) => number.trim())
              .filter((number) => /\d/.test(number))
          : Array.isArray(newValue)
            ? newValue
            : [];

      updatedRow.trackingNo = trackingNumbers;
    }

    const optimisticValue = field === "trackingNo" ? updatedRow.trackingNo : valueForServer;

    setTableData((currentRows) =>
      currentRows.map((item) =>
        item._id === row._id
          ? {
              ...item,
              [field]: optimisticValue,
            }
          : item,
      ),
    );

    try {
      await updatePriorityMoscow(updatedRow).unwrap();

      if (field === "trackingNo" && updatedRow.trackingNo?.length > 0) {
        await fetchTrackingStatuses(updatedRow.trackingNo, "register");
      }

      if (logModalOrderId === row._id) {
        refetchLogs();
      }

      showToast("Update has been done");
    } catch (updateError) {
      console.error("Update error:", updateError);

      setTableData((currentRows) =>
        currentRows.map((item) =>
          item._id === row._id
            ? {
                ...item,
                [field]: oldValue,
              }
            : item,
        ),
      );

      showToast("Failed to update row", "error");
    }
  };

  const confirmDelete = (id) => {
    setRowToDelete(id);
    setShowDeleteAlert(true);
  };

  const handleDelete = async () => {
    if (!rowToDelete) {
      return;
    }

    const deleteId = rowToDelete;

    try {
      await deletePriorityMoscow(deleteId).unwrap();

      setTableData((currentRows) => currentRows.filter((row) => row._id !== deleteId));

      setSelectedRows((currentRows) => currentRows.filter((id) => id !== deleteId));

      setShowDeleteAlert(false);
      setRowToDelete(null);

      showToast("Row deleted successfully");
    } catch (deleteError) {
      console.error("Failed to delete row:", deleteError);

      showToast("Failed to delete row", "error");
    }
  };

  const handleExportExcel = async (tableData, callback) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Sheet 1");

    const date = new Date(tableData.date);

    const formattedDate = date.toLocaleDateString("en-GB");

    const excelData = [
      ["ЗАЯВКА НА ОТГРУЗКУ СО СКЛАДА"],

      [`${tableData.clientName} ${tableData.orderNumber}`],

      [tableData.comments || ""],

      ["ИСПОЛНИТЕЛЬ: ООО «СЕЙФКИПИНГ»ИНН/КПП:7703802843/770301001 г.Москва, проезд Серебрякова,д. 14 Г телефон склада 8 (499)707-88-14"],

      ["Заявку составил (Ф.И.О.*): Sim Maksim"],

      ["Данные клиента (водителя,авто)*:", "", tableData.carrier || ""],

      [`Дата заявки    ${formattedDate}`],

      ["#", "Order", "QTY"],

      ["1", `${tableData.clientName} ${tableData.orderNumber}`, tableData.boxQty],
    ];

    excelData.splice(6, 0, [""]);

    excelData.forEach((row) => {
      worksheet.addRow(row);
    });

    worksheet.mergeCells("A1:D1");
    worksheet.mergeCells("A2:D2");
    worksheet.mergeCells("A3:D3");
    worksheet.mergeCells("A4:D4");
    worksheet.mergeCells("A5:D5");
    worksheet.mergeCells("A6:B6");
    worksheet.mergeCells("C6:D6");
    worksheet.mergeCells("A7:C7");
    worksheet.mergeCells("A8:C8");

    const firstMergedRow = worksheet.getCell("A1");

    firstMergedRow.alignment = {
      horizontal: "center",
      vertical: "middle",
      wrapText: true,
    };

    firstMergedRow.font = {
      bold: true,
      size: 12,
    };

    worksheet.getRow(4).height = 40;

    const secondMergedRow = worksheet.getCell("A2");

    secondMergedRow.alignment = {
      horizontal: "center",
      vertical: "middle",
      wrapText: true,
    };

    secondMergedRow.font = {
      bold: true,
      size: 18,
    };

    const thirdMergedRow = worksheet.getCell("A3");

    thirdMergedRow.alignment = {
      horizontal: "center",
      vertical: "middle",
      wrapText: true,
    };

    thirdMergedRow.font = {
      bold: true,
      size: 18,
    };

    thirdMergedRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: {
        argb: "FFD9D9D9",
      },
    };

    const fourthMergedRow = worksheet.getCell("A4");

    fourthMergedRow.font = {
      size: 12,
    };

    fourthMergedRow.alignment = {
      horizontal: "left",
      vertical: "middle",
      wrapText: true,
    };

    const fifthMergedRow = worksheet.getCell("A5");

    fifthMergedRow.font = {
      size: 10,
      color: {
        argb: "FF0000",
      },
    };

    const sixthMergedRow = worksheet.getCell("A6");

    sixthMergedRow.font = {
      size: 10,
      color: {
        argb: "FF0000",
      },
    };

    const seventhRow = worksheet.getCell("A7");

    seventhRow.font = {
      size: 14,
      bold: true,
    };

    seventhRow.alignment = {
      horizontal: "left",
      vertical: "middle",
      wrapText: true,
    };

    const carrierRow = worksheet.getCell("C6");

    carrierRow.font = {
      bold: true,
      size: 16,
      color: {
        argb: "FF0000",
      },
    };

    ["C9", "C10"].forEach((cellAddress) => {
      const cell = worksheet.getCell(cellAddress);

      cell.font = {
        bold: true,
        size: 18,
      };
    });

    worksheet.getColumn("A").width = 4;
    worksheet.getColumn("B").width = 50;
    worksheet.getColumn("C").width = 30;
    worksheet.getColumn("D").width = 30;
    worksheet.getColumn("E").width = 10;
    worksheet.getColumn("F").width = 20;
    worksheet.getColumn("G").width = 25;

    for (let rowIndex = 7; rowIndex < excelData.length; rowIndex += 1) {
      ["A", "D", "G"].forEach((column) => {
        const cell = worksheet.getCell(`${column}${rowIndex + 1}`);

        cell.alignment = {
          horizontal: "center",
          vertical: "middle",
          wrapText: true,
        };

        cell.font = {
          size: 14,
        };
      });

      const columnBCell = worksheet.getCell(`B${rowIndex + 1}`);

      columnBCell.alignment = {
        horizontal: "left",
        vertical: "middle",
        wrapText: true,
      };

      columnBCell.font = {
        size: 14,
      };

      for (let columnIndex = 0; columnIndex < excelData[rowIndex].length; columnIndex += 1) {
        const columnLetter = String.fromCharCode(65 + columnIndex);

        const cell = worksheet.getCell(`${columnLetter}${rowIndex + 1}`);

        cell.border = {
          top: {
            style: "thin",
          },

          left: {
            style: "thin",
          },

          bottom: {
            style: "thin",
          },

          right: {
            style: "thin",
          },
        };
      }
    }

    const buffer = await workbook.xlsx.writeBuffer();

    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    if (callback) {
      showToastFixed("Sending Email!");

      try {
        await callback(blob, tableData.clientName, tableData.orderNumber, tableData.carrier);

        await updatePriorityMoscow({
          _id: tableData._id,
          emailSent: true,
        }).unwrap();

        setTableData((currentRows) =>
          currentRows.map((row) =>
            row._id === tableData._id
              ? {
                  ...row,
                  emailSent: true,
                }
              : row,
          ),
        );

        toastr.clear();
        showToast("Email Sent!");
      } catch (emailError) {
        toastr.clear();

        console.error("Failed to send email:", emailError);

        showToast("Failed to send email", "error");
      }

      return;
    }

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;

    link.download = `ЗАЯВКА_${tableData.clientName}_${tableData.orderNumber}.xlsx`;

    link.click();

    URL.revokeObjectURL(url);
  };

  const handleBulkAction = async (selectedIds, actionValue) => {
    if (!selectedIds?.length || !actionValue) {
      return;
    }

    try {
      if (actionValue.startsWith("status:")) {
        const statusValue = actionValue.split(":")[1];

        await Promise.all(
          selectedIds.map((id) =>
            updatePriorityMoscow({
              _id: id,
              status: statusValue,
              managerID: profileData?._id,
            }).unwrap(),
          ),
        );

        setTableData((currentRows) =>
          currentRows.map((row) =>
            selectedIds.includes(row._id)
              ? {
                  ...row,
                  status: statusValue,
                }
              : row,
          ),
        );
      }

      setSelectedRows([]);

      showToast("Bulk update has been done");
    } catch (bulkError) {
      console.error("Bulk update error:", bulkError);

      showToast("Failed to update selected rows", "error");
    }
  };

  const handleGlobalActionSubmit = async (event, values) => {
    await handleBulkAction(selectedRows, values.action);
  };

  const STATUS_OPTIONS = [
    {
      key: "under_work",
      label: "Under Work",

      icon: <BicepsFlexed size={18} className="me-2" />,

      colorClass: "bg-primary",
    },

    {
      key: "request_to_pack",
      label: "Sent Request to Pack",

      icon: <FileBox size={18} className="me-2" />,

      colorClass: "bg-warning",
    },

    {
      key: "half_pack",
      label: "Half Pack",

      icon: <Rows2 size={18} className="me-2" />,

      colorClass: "pink-color",
    },

    {
      key: "packaged",
      label: "Packaged",

      icon: <Package size={18} className="me-2" />,

      colorClass: "bg-info bg-gradient",
    },

    {
      key: "request_slip",
      label: "Requested Slip/Docs",

      icon: <File size={18} className="me-2" />,

      colorClass: "light_blue",
    },

    {
      key: "wait_pickup",
      label: "Waiting Pickup",

      icon: <AlarmClock size={18} className="me-2" />,

      colorClass: "pink-color",
    },

    {
      key: "part_sent",
      label: "Partially Sent",

      icon: <Boxes size={18} className="me-2" />,

      colorClass: "bg-success bg-gradient",
    },

    {
      key: "sent",
      label: "Sent",

      icon: <CheckCircle size={18} className="me-2" />,

      colorClass: "bg-success bg-gradient",
    },

    {
      key: "waiting_compensation_fee",
      label: "Waiting Compensation Fee",

      icon: <MessageCircleReply size={18} className="me-2" />,

      colorClass: "bg-dark bg-opacity-75",
    },

    {
      key: "draft_email",
      label: "Draft Email",

      icon: <NotepadTextDashed size={18} className="me-2" />,

      colorClass: "bg-secondary",
    },

    {
      key: "stopped",
      label: "Stopped",

      icon: <AlertCircle size={18} className="me-2" />,

      colorClass: "bg-danger bg-gradient",
    },

    {
      key: "only_pack",
      label: "ONLY PACK",

      icon: <PackageMinus size={18} className="me-2" />,

      colorClass: "bg-primary",
    },

    {
      key: "only_pack_under_work",
      label: "ONLY PACK - Under Work",

      icon: <BicepsFlexed size={18} className="me-2" />,

      colorClass: "bg-primary",
    },

    {
      key: "only_pack_req_to_pack",
      label: "ONLY PACK - Sent request to Pack",

      icon: <FileBox size={18} className="me-2" />,

      colorClass: "bg-primary",
    },

    {
      key: "only_pack_packaged",
      label: "ONLY PACK - Packaged",

      icon: <Package size={18} className="me-2" />,

      colorClass: "bg-primary",
    },

    {
      key: "only_pack_allow_send",
      label: "ONLY PACK - Allow to Send",

      icon: <HandCoins size={18} className="me-2" />,

      colorClass: "bg-success",
    },
  ];

  const BULK_STATUS_OPTIONS = STATUS_OPTIONS.map((status) => ({
    value: `status:${status.key}`,
    text: status.label,
    className: status.colorClass,
  }));

  const getStatusMeta = (key) => STATUS_OPTIONS.find((status) => status.key === key);

  const columns = [
    {
      text: "ID",
      dataField: "_id",
      sort: true,
      csvExport: false,
      editable: false,

      formatter: (cell, row, rowIndex) => rowIndex + 1,

      style: {
        width: "20px",
      },
    },

    {
      text: (
        <div>
          Invoice
          <br />
          Amount
        </div>
      ),

      dataField: "DocumentAmount",
      sort: true,
      editable: true,

      formatter: (cellContent, order) => (
        <h5 className="font-size-16 mb-1">
          {order.DocumentAmount} {order.Currency}
        </h5>
      ),
    },

    {
      text: "Client Name",
      dataField: "clientName",
      sort: true,
      editable: true,

      formatter: (cellContent, order) => <h5 className="font-size-16 mb-1">{order.clientName}</h5>,
    },

    {
      text: "InvoiceNo",
      dataField: "orderNumber",
      sort: true,
      editable: true,

      formatter: (cellContent, order) => (
        <h5
          className="font-size-16 mb-1"
          style={{
            whiteSpace: "nowrap",
            overflowX: "auto",
            display: "block",
          }}
        >
          {order.orderNumber}
        </h5>
      ),
    },

    {
      text: (
        <div>
          Box
          <br />
          Qty
        </div>
      ),

      dataField: "boxQty",
      sort: true,
      editable: true,

      formatter: (cellContent, order) => <h5 className="font-size-16 mb-1 text-center">{order.boxQty} кор.</h5>,
    },

    {
      text: "Box Size",
      dataField: "boxSize",
      sort: true,
      editable: true,

      formatter: (cellContent, order) => <h5 className="text-break text-center">{order.boxSize}</h5>,
    },

    {
      text: "Date",
      dataField: "date",
      sort: true,
      csvExport: true,
      hidden: false,

      csvFormatter: (cell, row) => {
        if (!row.date) {
          return "";
        }

        const dateValue = new Date(row.date);

        return `${dateValue.toLocaleDateString("en-GB")} ${dateValue.toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
        })}`;
      },

      formatter: (cell, row) => {
        if (!cell) {
          return "";
        }

        const placedDate = new Date(row.date);

        const sendDate = row.sendDate ? new Date(row.sendDate) : null;

        const sameDay = sendDate && placedDate.getFullYear() === sendDate.getFullYear() && placedDate.getMonth() === sendDate.getMonth() && placedDate.getDate() === sendDate.getDate();

        const formatted = `${placedDate.toLocaleDateString("en-GB")} ${placedDate.toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
        })}`;

        const tooltipText = sameDay ? "Send date matches order date" : "⚠ Send date is later than Placed date";

        if (!sendDate || sameDay) {
          return <span title={tooltipText}>{formatted}</span>;
        }

        return (
          <span title={tooltipText}>
            <span
              style={{
                color: "#d9534f",
                fontWeight: "bold",
              }}
            >
              {formatted}

              <AlertTriangle
                size={20}
                color="#d9534f"
                strokeWidth={2}
                style={{
                  marginLeft: "6px",
                  marginBottom: "6px",
                }}
              />
            </span>
          </span>
        );
      },

      editor: {
        type: Type.DATE,
      },
    },

    {
      text: "Send Date",
      dataField: "sendDate",
      sort: true,
      csvExport: true,
      hidden: false,

      csvFormatter: (cell, row) => (row.sendDate ? new Date(row.sendDate).toLocaleDateString("en-GB") : ""),

      formatter: (cell, row) => {
        if (!cell) {
          return (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                lineHeight: "1.2",
              }}
              title='Put "Send date" or ONLY PACK to place the order'
            >
              {/* <BadgeInfo size={32} color="rgb(217, 83, 79)" /> */}
              <span
                style={{
                  color: "red",
                  fontWeight: "bold",
                  fontSize: "12px",
                  textAlign: "center",
                  marginBottom: "4px",
                }}
              >
                PUT "SEND DATE" OR ONLY PACK <br /> TO PLACE THE ORDER
              </span>
            </div>
          );
        }

        const placedDate = new Date(row.date);

        const sendDate = new Date(row.sendDate);

        const sameDay = placedDate.getFullYear() === sendDate.getFullYear() && placedDate.getMonth() === sendDate.getMonth() && placedDate.getDate() === sendDate.getDate();

        const formatted = `${sendDate.toLocaleDateString("en-GB")} ${sendDate.toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
        })}`;

        if (sameDay) {
          return formatted;
        }

        return (
          <span title="⚠ Send date is later than Placed date">
            <span
              style={{
                color: "#d9534f",
                fontWeight: "bold",
              }}
            >
              {formatted}

              <AlertTriangle
                size={20}
                color="#d9534f"
                strokeWidth={2}
                style={{
                  marginLeft: "6px",
                  marginBottom: "6px",
                }}
              />
            </span>
          </span>
        );
      },

      editor: {
        type: Type.DATE,

        defaultValue: new Date().toISOString().split("T")[0],
      },
    },

    {
      text: "Status",
      dataField: "status",
      sort: true,
      editable: false,

      formatter: (cellContent, order) => {
        const meta = getStatusMeta(order.status);

        const isAdmin = profileData?.accType === "Admin";

        const isLogisticsUA = profileData?.department?.depName === "Logistics Ukraine";

        const isSales = profileData?.department?.depName === "Sales Department";

        const whManagerId = typeof order.whManager === "object" ? order.whManager?._id : order.whManager;

        const isWhManager = String(whManagerId || "") === String(profileData?._id || "");

        // Admin может всё
        // WH Manager может всё
        // Sales может открыть select, но только для разрешённых статусов
        const canEdit = isAdmin || isWhManager || isSales;

        const hasRealizationNo = Boolean(String(order.realizationNo || "").trim());

        const SALES_ALLOWED_STATUSES = ["stopped", "only_pack", "only_pack_allow_send"];

        return (
          <div>
            <div
              className={`d-flex align-items-center justify-content-between rounded-pill px-3 py-2 fw-bold text-white ${meta?.colorClass || "bg-secondary"}`}
              style={{
                width: "290px",
                gap: "8px",
                position: "relative",
                fontSize: "16px",
                textAlign: "center",
                marginTop: "12px",
                marginBottom: "12px",
              }}
            >
              {/* Icon + Label */}
              <div
                className="d-flex align-items-center justify-content-center"
                style={{
                  gap: "4px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                }}
              >
                {!order.status && <BadgePlus size={16} color="orange" />}

                {meta?.icon}

                <span>{meta?.label || "Set Status"}</span>
              </div>

              {/* Показываем стрелку только если можно менять статус */}
              {canEdit && <ChevronDown size={16} className="ms-2" />}

              {/* Invisible select поверх всей кнопки */}
              {canEdit && (
                <select
                  value={order.status || ""}
                  onChange={(event) => {
                    const newStatus = event.target.value;

                    // Дополнительная защита для Sales
                    if (isSales && !isAdmin && !SALES_ALLOWED_STATUSES.includes(newStatus)) {
                      return;
                    }

                    // Sent нельзя без Realization No
                    if (newStatus === "sent" && !hasRealizationNo) {
                      return;
                    }

                    handleSave(order.status, newStatus, order, {
                      dataField: "status",
                    });
                  }}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    opacity: 0,
                    cursor: "pointer",
                  }}
                >
                  <option value="">Set Status</option>

                  {STATUS_OPTIONS.map((option) => {
                    const isSentDisabled = option.key === "sent" && !hasRealizationNo;

                    const isDisabledForSales = isSales && !isAdmin && !SALES_ALLOWED_STATUSES.includes(option.key);

                    const isDisabled = isSentDisabled || isDisabledForSales;

                    return (
                      <option
                        key={option.key}
                        value={option.key}
                        disabled={isDisabled}
                        className={isSentDisabled ? "text-danger fw-bold" : ""}
                        style={
                          isSentDisabled
                            ? {
                                color: "red",
                                fontWeight: 700,
                              }
                            : undefined
                        }
                      >
                        {isSentDisabled ? "⚠ Sent - Realization No required" : option.label}
                      </option>
                    );
                  })}
                </select>
              )}
            </div>
          </div>
        );
      },

      style: {
        width: "290px",
      },
    },

    {
      text: "Address",
      dataField: "address",
      sort: true,
      editable: true,

      formatter: (cellContent, order) => {
        if (!cellContent) {
          return (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                lineHeight: "1.2",
              }}
              title='Put "Address" or ONLY PACK to place the order'
            >
              {/* <BadgeInfo size={32} color="rgb(217, 83, 79)" /> */}
              <span
                style={{
                  color: "red",
                  fontWeight: "bold",
                  fontSize: "12px",
                  textAlign: "center",
                  marginBottom: "4px",
                }}
              >
                PUT "ADDRESS" OR ONLY PACK <br /> TO PLACE THE ORDER
              </span>
            </div>
          );
        }

        return (
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="text-break name-width">{order.address}</h5>

            {order.address && (
              <Copy
                size={20}
                color="#497ea7"
                style={{
                  cursor: "pointer",
                }}
                onClick={(event) => {
                  event.stopPropagation();

                  navigator.clipboard
                    .writeText(order.address)
                    .then(() => {
                      showToast("Copied to clipboard");
                    })
                    .catch(() => {
                      showToast("Failed to copy address", "error");
                    });
                }}
              />
            )}
          </div>
        );
      },
    },

    {
      text: "Carrier",
      dataField: "carrier",
      sort: true,
      editable: false,

      formatter: (cellContent, order) => (
        <div className="form-group mt-2 mb-2">
          {!order.carrier && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                lineHeight: "1.2",
                marginBottom: "6px",
              }}
              title='Select "Carrier" or ONLY PACK to place the order'
            >
              <span
                style={{
                  color: "red",
                  fontWeight: "bold",
                  fontSize: "12px",
                  textAlign: "center",
                }}
              >
                SELECT "CARRIER" OR ONLY PACK
                <br />
                TO PLACE THE ORDER
              </span>
            </div>
          )}

          <div className="d-flex align-items-center">
            <select
              className="form-control status-selection"
              value={order.carrier || ""}
              name="carrier"
              onChange={(event) =>
                handleSave(order.carrier, event.target.value, order, {
                  dataField: "carrier",
                })
              }
            >
              <option value="">Select carrier</option>

              <option value="CDEK (CLIENT PAY FEE by CASH)">CDEK (CLIENT PAY FEE by CASH)</option>

              <option value="CDEK (WE PAY FEE)">CDEK (WE PAY FEE)</option>

              <option value="Client Provide Slip">CLIENT PROVIDE SLIP</option>

              <option value="YANDEXGO-IN MOSCOW (WE PAY FEE)">YANDEXGO-IN MOSCOW (WE PAY FEE)</option>

              <option value="YANDEXGO-IN MOSCOW (CLIENT PAY FEE by CASH)">YANDEXGO-IN MOSCOW (CLIENT PAY FEE by CASH)</option>

              <option value="DONT SEND TO CLIENT">DONT SEND TO CLIENT</option>

              <option value="CLIENT PICKUP">CLIENT PICKUP</option>
            </select>

            {!order.carrier && <BadgePlus size={20} color="orange" className="ms-2 flex-shrink-0" />}
          </div>
        </div>
      ),
    },

    {
      text: "Realization No",
      dataField: "realizationNo",
      sort: true,
      editable: true,

      formatter: (cellContent, order) => <h5 className="font-size-16 mb-1 text-center">{order.realizationNo || ""}</h5>,
    },

    {
      text: "Tracking No",
      dataField: "trackingNo",
      sort: true,
      editable: true,

      formatter: (cellContent, order) => <TrackingColumnPrTest tableData={order} />,
    },

    {
      text: "Sales Manager",
      dataField: "managers",
      sort: true,
      hidden: false,
      editable: false,

      csvFormatter: (cell, row) => {
        const manager = Array.isArray(row.managers) ? row.managers.find(Boolean) : row.managers;

        if (!manager) {
          return "";
        }

        return `${manager.fname || ""} ${manager.sname || ""}`.trim();
      },

      formatter: (cellContent, order) => {
        const manager = Array.isArray(order.managers) ? order.managers.find(Boolean) : order.managers;

        return <h5 className="font-size-16 mb-1">{manager ? `${manager.fname || ""} ${manager.sname || ""}`.trim() : ""}</h5>;
      },
    },

    {
      text: "Comments",
      dataField: "comments",
      sort: true,
      editable: true,

      formatter: (cellContent, order) => <h5 className="font-size-16 mb-1 text-center">{order.comments}</h5>,
    },

    {
      text: "Actions",
      dataField: "actions",
      isDummyField: true,
      editable: false,
      csvExport: false,

      formatter: (cellContent, order) => {
        const isAdmin = profileData?.accType === "Admin";

        const isSales = profileData?.department?.depName === "Sales Department";

        return (
          <div
            style={{
              display: "flex",
              gap: "12px",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isAdmin && (
              <>
                <FileDown
                  size={20}
                  title="Download Excel"
                  style={{
                    cursor: "pointer",
                  }}
                  onClick={() => handleExportExcel(order)}
                />

                {order.emailSent ? (
                  <MailOpen
                    size={20}
                    color="green"
                    title="Send Email"
                    style={{
                      cursor: "pointer",
                    }}
                    onClick={() => handleExportExcel(order, sendEmailWithExcel)}
                  />
                ) : (
                  <Mail
                    size={20}
                    color="blue"
                    title="Send Email"
                    style={{
                      cursor: "pointer",
                    }}
                    onClick={() => handleExportExcel(order, sendEmailWithExcel)}
                  />
                )}

                <ScrollText
                  size={20}
                  color="#0d6efd"
                  title="View Logs"
                  style={{
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    setLogModalOrderId(order._id);

                    setShowLogModal(true);
                  }}
                />
              </>
            )}

            {(isAdmin || isSales) && (
              <Trash2
                size={20}
                color="red"
                title="Delete"
                style={{
                  cursor: "pointer",
                }}
                onClick={() => confirmDelete(order._id)}
              />
            )}
          </div>
        );
      },
    },
  ];

  const selectRow = {
    mode: "checkbox",
    selected: selectedRows,

    onSelect: (row, isSelect) => {
      setSelectedRows((currentRows) => {
        if (isSelect) {
          return currentRows.includes(row._id) ? currentRows : [...currentRows, row._id];
        }

        return currentRows.filter((id) => id !== row._id);
      });
    },

    onSelectAll: (isSelect, rows) => {
      setSelectedRows(isSelect ? rows.map((row) => row._id) : []);
    },
  };

  const defaultSorted = [
    {
      dataField: "date",
      order: "asc",
    },
  ];

  if (error) {
    return <p>Error loading data</p>;
  }

  return (
    <React.Fragment>
      {isLoading && <PageLoader />}

      <div className="page-content">
        <MetaTags>
          <title>Priority Moscow</title>
        </MetaTags>

        <Container fluid>
          <Breadcrumbs title="Korupharma" breadcrumbItem="Priority Moscow" />

          <Row className="card-section">
            <Col lg="12">
              <div className="pcss3t logtb pcss3t-effect-scale pcss3t-theme-1">
                <ul>
                  <AnimatePresence key="priority-moscow" exitBeforeEnter>
                    <motion.li
                      className="tab-content tab-content-1 typography"
                      initial={{
                        opacity: 0,
                        x: 30,
                      }}
                      animate={{
                        opacity: 1,
                        x: 0,
                      }}
                      exit={{
                        opacity: 0,
                        x: -30,
                      }}
                      transition={{
                        duration: 0.3,
                      }}
                      style={{
                        display: "block",
                      }}
                    >
                      <div id="card-section" ref={cardRef}>
                        <Row className="card-section">
                          <Col lg="12">
                            <Card>
                              <CardBody className="cardBodySticky">
                                <ToolkitProvider keyField="_id" data={filteredData} columns={columns} search>
                                  {(props) => (
                                    <React.Fragment>
                                      <MainContent
                                        page={{
                                          pg: "priorityMoscow",

                                          props,

                                          cardHeight,

                                          showSearch: true,

                                          showCSV: true,

                                          showInTblFltr: true,

                                          showDbFilter: false,

                                          showBtn: false,

                                          columns: props.baseProps.columns,

                                          data: props.baseProps.data,

                                          defaultSorted,

                                          selectRow,

                                          cellEdit: cellEditFactory({
                                            mode: window.innerWidth < 768 ? "click" : "dbclick",

                                            blurToSave: true,

                                            afterSaveCell: handleSave,
                                          }),
                                        }}
                                        infoPopoverOptions={[
                                          {
                                            isAdmin: profileData?.accType === "Admin",

                                            boardKey: "board-general",

                                            notify: true,

                                            adminOnly: false,

                                            userId: profileData?._id,
                                          },

                                          {
                                            isAdmin: profileData?.accType === "Admin",

                                            boardKey: "board-admin",

                                            boardImageType: "admin",

                                            adminOnly: true,

                                            userId: profileData?._id,
                                          },
                                        ]}
                                        filterOptions={{
                                          status: STATUS_OPTIONS,
                                        }}
                                        notificationSettings={{
                                          showNotification: true,

                                          keyword: "priorityMoscow",

                                          module: "Bot Priority Notification",
                                        }}
                                        setNoDbFilters={setNoDbFilters}
                                        tableOptions={{
                                          includeSelect: true,

                                          manualColumnWidthsIndex: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],

                                          manualColumnWidths: [
                                            55, // ID
                                            130, // Invoice Amount
                                            190, // Client Name
                                            145, // Invoice No
                                            80, // Box Qty
                                            140, // Box Size
                                            160, // Date
                                            160, // Send Date
                                            250, // Status
                                            320, // Address
                                            450, // Carrier
                                            160, // Realization No
                                            300, // Tracking No
                                            200, // Sales Manager
                                            240, // Comments
                                            160, // Actions
                                          ],
                                        }}
                                        tableActions={{
                                          globalActionOptions: BULK_STATUS_OPTIONS,

                                          globalActionBtnLbl: "APPLY",

                                          handleGlobalActionSubmit,
                                        }}
                                      />

                                      <SweetAlert
                                        show={showDeleteAlert}
                                        danger
                                        showCancel
                                        confirmBtnText="Yes, delete it!"
                                        confirmBtnBsStyle="danger"
                                        title="Are you sure?"
                                        onConfirm={handleDelete}
                                        onCancel={() => {
                                          setShowDeleteAlert(false);

                                          setRowToDelete(null);
                                        }}
                                        focusCancelBtn
                                      >
                                        The order will be deleted from the system!
                                      </SweetAlert>

                                      <Modal isOpen={showLogModal} toggle={() => setShowLogModal(false)} size="xl" className="log-modal modal-lg-custom">
                                        <ModalHeader toggle={() => setShowLogModal(false)}>
                                          <h3>Order Logs</h3>
                                        </ModalHeader>

                                        <ModalBody>
                                          {logsLoading ? (
                                            <p>Loading logs...</p>
                                          ) : logs.length === 0 ? (
                                            <p>No logs found for this order.</p>
                                          ) : (
                                            <div className="log-timeline">
                                              {logs.map((log) => (
                                                <div key={log._id} className="timeline-item">
                                                  <div className="timeline-dot" />

                                                  <div className="timeline-content">
                                                    <small className="text-muted">
                                                      {new Date(log.timestamp).toLocaleString("en-GB", {
                                                        hour: "2-digit",

                                                        minute: "2-digit",

                                                        day: "2-digit",

                                                        month: "2-digit",

                                                        year: "numeric",
                                                      })}

                                                      {log.createdBy?.fname && ` • ${log.createdBy.fname} ${log.createdBy.sname}`}
                                                    </small>

                                                    <strong>{log.action}</strong>

                                                    <p className="log-details">{log.details}</p>
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </ModalBody>
                                      </Modal>
                                    </React.Fragment>
                                  )}
                                </ToolkitProvider>
                              </CardBody>
                            </Card>
                          </Col>
                        </Row>
                      </div>
                    </motion.li>
                  </AnimatePresence>
                </ul>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
}
